/**
 * taxAndLoanUtils.ts – loan repayment and tax/CGT helpers.
 * Ported verbatim from frontend/src/calculations/taxAndLoanUtils.js.
 */
import { TAX_BRACKETS } from './constants/ratesAndThresholds.ts';
import { computeDisposalCgt, taxContextFromAssessableIncomes } from './disposalCgt.ts';
import type { FactFindAsset } from './factFindTypes.ts';

export function calculateLoanRepayment(
    balance: number,
    annualRate: number,
    termYears: number,
    frequency: string = 'monthly',
    loanType: string = 'pi',
): number {
    if (!balance || !annualRate || !termYears) return 0;

    const L = balance;
    const r_annual = annualRate / 100;

    let n_periods_per_year: number;

    switch (frequency) {
        case 'weekly': n_periods_per_year = 52; break;
        case 'fortnightly': n_periods_per_year = 26; break;
        default: n_periods_per_year = 12; // monthly
    }

    const r_periodic = r_annual / n_periods_per_year;
    const n_total_periods = termYears * n_periods_per_year;

    if (loanType === 'io') { // Interest Only
        return L * r_periodic;
    }

    // Principal & Interest
    if (r_periodic === 0) return L / n_total_periods;

    const numerator = r_periodic * Math.pow(1 + r_periodic, n_total_periods);
    const denominator = Math.pow(1 + r_periodic, n_total_periods) - 1;

    if (denominator === 0) return 0;

    return L * (numerator / denominator);
}

export function calculateTax(income: number): number {
    for (let i = TAX_BRACKETS.length - 1; i >= 0; i--) {
        const b = TAX_BRACKETS[i]!;
        if (income > (i === 0 ? 0 : TAX_BRACKETS[i - 1]!.limit)) {
            return b.base + (income - (i === 0 ? 0 : TAX_BRACKETS[i - 1]!.limit)) * b.rate;
        }
    }
    return 0;
}

export function calculateMarginalTaxRate(assessableIncome: number): number {
    for (let i = TAX_BRACKETS.length - 1; i >= 0; i--) {
        if (assessableIncome > (i === 0 ? 0 : TAX_BRACKETS[i - 1]!.limit)) {
            return TAX_BRACKETS[i]!.rate;
        }
    }
    return 0;
}

/**
 * CGT on a (possibly partial) disposal during a projection year.
 *
 * Delegates to the single CGT engine (./disposalCgt.ts). `simulatedFinancials` supplies the household
 * tax position for that year — its `taxContext` (returned by `calculateFinancials`) is used when
 * present, so the gain is taxed with the year's own indexed brackets, ages and SAPTO band.
 *
 * `disposalDate`/`cpiRate` let a projection tax a FUTURE-year sale under the right regime (sales after
 * 1-Jul-2027 index the post-commencement slice instead of taking the 50% discount).
 *
 * Previously this had its own copy of the math and returned ZERO for any ownership that was not
 * client/partner/joint — silently applying no CGT to entity-owned assets. The engine now taxes those
 * according to the owning structure.
 */
export function calculateCgt(
    asset: FactFindAsset,
    saleAmount: number,
    simulatedFinancials: any,
    { disposalDate, cpiRate }: { disposalDate?: Date | string; cpiRate?: number } = {},
): number {
    const value = asset?.value || 0;
    if (!value || saleAmount <= 0) return 0;

    // `taxContext` is present on every `calculateFinancials` result; the fallback only covers a stale
    // payload produced before it existed, and loses SAPTO/Medicare precision (see its own doc comment).
    const taxContext =
        simulatedFinancials?.taxContext ??
        taxContextFromAssessableIncomes(
            simulatedFinancials?.clientAssessableIncome || 0,
            simulatedFinancials?.partnerAssessableIncome || 0,
            asset?.ownership === 'joint' || asset?.ownership === 'partner',
        );

    return computeDisposalCgt({
        taxContext,
        asset,
        salePrice: saleAmount,
        // Brokerage on a share sale is immaterial at this level of estimate.
        sellingCosts: 0,
        originalAssetValue: value,
        disposalDate,
        cpiRate,
        structures: simulatedFinancials?.structures,
    }).estimatedCGT;
}
